/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import cstmTable from '../../../../../share/components/vue/PaginationTable';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import config from '../../../../../share/config';
import session from '../../../../../share/session';
import error from '../../../../../common/nodejs/errors';
import { printContent } from '../../../../../share/util/printUtils';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  FAILED: 'failed',
  RESULT: 'result',
  NO_SUPPORTED: 'no_supported'
};

export default {
  name: 'requirementSpABG001',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      displayInfo: [],
      dialogSize: 'md',
      modal_special_requirement_ABG1: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  
  components: {
    'cstm-table': cstmTable,
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    },
    resultTableColumns() {
      return [
        { name: 'id', label: `individual.header_item_ABG1`, useResource: true },
        { name: 'value', label: `individual.header_value_ABG1` },
      ];
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_special_requirement_ABG1 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_special_requirement_ABG1 = false;
    },

    /** データ読み出し処理 */
    async execute() {
      try {
        this.isLoading = true;

        //バックエンドAPI POST,GET
        const token = session.getToken();
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read',
            is_special: true
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              //成功
              // 正常終了：読み出しデータ表示
              this.dialogSize = 'lg';
              this.phase = PHASE.RESULT;
              this.$nextTick(() => {
                this.displayInfo = response.data.user_data.support_list.map((data) => 
                  {
                    return {
                      id: `spwsDid${systemId}.${data.id}`,
                      value: data.data_type === 'HEX' ? `0x${data.value.toUpperCase()}` : data.value  // data_type:Hexの場合、"0x"を付与する 
                    };
                  }
                );
              });
              break;
            case config.STATUS_WSP_FAILED:
              switch (response.data.async_code) {
                case error.WSP_GENERAL_ERROR:
                  // 一般エラーの場合、サポートデータなしエラーとする
                  this.phase = PHASE.NO_SUPPORTED;
                  break;
                default:
                  // それ以外の場合、通信エラーを表示
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              // completedでもfailedでもない場合、通信エラーを表示する
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },


    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /** 印刷プレビュー画面表示 */
    startPrint() {
      // 印刷部分の表示内容を取得
      const element = document.getElementById('print-area');
      // タイトル
      const title = this.$t(`${this.i18nWsKey}.menu_title`);
      // 帳票印刷処理
      printContent(element, title, true);
		}
  },

  async mounted() {
    this.init();
    // データ読み出し
    this.execute();
  },
};